import { humanizeKey } from "@/icm/labels";
import type {
  IcmLineItem,
  IcmPayoutBreakdown,
  IcmPayoutBreakdownItem,
} from "@/icm/types";
import {
  getPayoutBreakdownEntryDisplayType,
  getPayoutBreakdownEntryValue,
  normalizePayoutRuleName,
} from "@/icm/utils";

type IcmStatementWithPayoutBreakdown = {
  payout_breakdown: IcmPayoutBreakdown | null;
  line_items?: IcmLineItem[];
};

const normalizeLineItems = (lineItems: IcmLineItem[]) =>
  lineItems.map((lineItem) => ({
    ...lineItem,
    payout_rule_name: normalizePayoutRuleName(lineItem.payout_rule_name),
  }));

const buildPayoutBreakdownItems = (
  payoutBreakdown?: IcmPayoutBreakdown | null,
) =>
  Object.entries(payoutBreakdown ?? {}).flatMap(([key, entry]) => {
    const value = getPayoutBreakdownEntryValue(entry);

    if (value === null) return [];

    return [
      {
        key,
        label: humanizeKey(key),
        value,
        displayType: getPayoutBreakdownEntryDisplayType(entry),
      },
    ];
  });

export const enrichIcmStatement = <T extends IcmStatementWithPayoutBreakdown>(
  statement: T,
): T & { payout_breakdown_items: IcmPayoutBreakdownItem[] } => {
  const payout_breakdown_items = buildPayoutBreakdownItems(
    statement.payout_breakdown,
  );

  if (!statement.line_items) {
    return {
      ...statement,
      payout_breakdown_items,
    };
  }

  return {
    ...statement,
    line_items: normalizeLineItems(statement.line_items),
    payout_breakdown_items,
  };
};
